import axios from 'axios';
import { 
  Intervention, 
  Campaign, 
  Site, 
  Technicentre, 
  Prestation, 
  AuthResponse, 
  ApiResponse,
  EmailData,
  EmailResponse,
  ValidationLog,
  AuditLog,
  FilterParams
} from '../types';

// ===== Service api =====

const API_BASE_URL = '/api';

const api = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

export const authApi = {
  login: async (password: string): Promise<AuthResponse> => {
    const response = await api.post('/auth/login', { password });
    return response.data;
  },

  logout: async (): Promise<void> => {
    await api.post('/auth/logout');
  },

  checkAuth: async (): Promise<AuthResponse> => {
    const response = await api.get('/auth/check');
    return response.data;
  },

  verifyGOF: async (password: string): Promise<{ success: boolean; error?: string }> => {
    const response = await api.post('/auth/verify-gof', { password });
    return response.data;
  },
};

export const interventionsApi = {
  getAll: async (params?: FilterParams): Promise<ApiResponse<Intervention[]>> => {
    const queryParams = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value) queryParams.append(key, value);
      });
    }
    const response = await api.get(`/interventions${queryParams.toString() ? '?' + queryParams.toString() : ''}`);
    return response.data;
  },

  create: async (intervention: Partial<Intervention>): Promise<{ success: boolean; id?: number; error?: string }> => {
    const response = await api.post('/interventions', intervention);
    return response.data;
  },

  update: async (id: number, intervention: Partial<Intervention>): Promise<{ success: boolean; error?: string }> => {
    const response = await api.put(`/interventions/${id}`, intervention);
    return response.data;
  },

  delete: async (id: number): Promise<{ success: boolean; error?: string }> => {
    const response = await api.delete(`/interventions/${id}`);
    return response.data;
  },

  getHistory: async (id: number): Promise<any[]> => {
    const response = await api.get(`/interventions/${id}/historique`);
    return response.data;
  },

  getGlobalHistory: async (): Promise<any[]> => {
    const response = await api.get('/interventions/historique/all');
    return response.data;
  },
};

export const campaignsApi = {
  getAll: async (): Promise<Campaign[]> => {
    const response = await api.get('/campaigns');
    return response.data;
  },

  create: async (campaign: { nom: string }): Promise<{ success: boolean; id?: number; error?: string }> => {
    const response = await api.post('/campaigns', campaign);
    return response.data;
  },

  update: async (id: number, campaign: { nom: string }): Promise<{ success: boolean; error?: string }> => {
    const response = await api.put(`/campaigns/${id}`, campaign);
    return response.data;
  },

  delete: async (id: number): Promise<{ success: boolean; error?: string }> => {
    const response = await api.delete(`/campaigns/${id}`);
    return response.data;
  },
};

export const sitesApi = {
  getAll: async (): Promise<Site[]> => {
    const response = await api.get('/sites');
    return response.data;
  },

  create: async (site: { nom: string }): Promise<{ success: boolean; id?: number; error?: string }> => {
    const response = await api.post('/sites', site);
    return response.data;
  },

  update: async (id: number, site: { nom: string }): Promise<{ success: boolean; error?: string }> => {
    const response = await api.put(`/sites/${id}`, site);
    return response.data;
  },

  delete: async (id: number): Promise<{ success: boolean; error?: string }> => {
    const response = await api.delete(`/sites/${id}`);
    return response.data;
  },
};

export const technicentresApi = {
  getAll: async (): Promise<Technicentre[]> => {
    const response = await api.get('/technicentres');
    return response.data;
  },

  create: async (technicentre: { nom: string; email?: string }): Promise<{ success: boolean; id?: number; error?: string }> => {
    const response = await api.post('/technicentres', technicentre);
    return response.data;
  },

  update: async (id: number, technicentre: { nom: string; email?: string }): Promise<{ success: boolean; error?: string }> => {
    const response = await api.put(`/technicentres/${id}`, technicentre);
    return response.data;
  },

  delete: async (id: number): Promise<{ success: boolean; error?: string }> => {
    const response = await api.delete(`/technicentres/${id}`);
    return response.data;
  },

  updatePassword: async (technicentreId: number, newPassword: string): Promise<{ success: boolean; message?: string; error?: string }> => {
    const response = await api.post('/auth/update-technicentre-password', {
      technicentre_id: technicentreId,
      new_password: newPassword
    });
    return response.data;
  },
};

export const prestationsApi = {
  getAll: async (): Promise<Prestation[]> => {
    const response = await api.get('/prestations');
    return response.data;
  },

  create: async (prestation: { nom: string }): Promise<{ success: boolean; id?: number; error?: string }> => {
    const response = await api.post('/prestations', prestation);
    return response.data;
  },

  update: async (id: number, prestation: { nom: string }): Promise<{ success: boolean; error?: string }> => {
    const response = await api.put(`/prestations/${id}`, prestation);
    return response.data;
  },

  delete: async (id: number): Promise<{ success: boolean; error?: string }> => {
    const response = await api.delete(`/prestations/${id}`);
    return response.data;
  },
};

export const emailApi = {
  sendEmail: async (data: EmailData): Promise<EmailResponse> => {
    const response = await api.post('/email/send', data);
    return response.data;
  },

  getEmailLogs: async (): Promise<any[]> => {
    const response = await api.get('/email/logs');
    return response.data;
  },

  logPowerAutomate: async (data: any): Promise<{ success: boolean }> => {
    const response = await api.post('/email/log-power-automate', data);
    return response.data;
  },
};

export const validationApi = {
  getLogs: async (): Promise<ValidationLog[]> => {
    const response = await api.get('/validation/logs');
    return response.data;
  },
};

export const auditApi = {
  getLogs: async (): Promise<AuditLog[]> => {
    const response = await api.get('/audit/logs');
    return response.data;
  },
};

export default api;
